import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "./Constants";

const TutorForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    role: "tutor",
    qualification: "",
    tutor_subject: "",
    experience: "",
  });


  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiErrorMessage, setApiErrorMessage] = useState("");

  // Tutor Subjects
  const tutorSubjects = ["PHYSICS", "CHEMISTRY", "MATHS", "BIOLOGY", "ENGLISH"];
  
  // Experience levels
  const experienceLevels = [
    "Less than 1 year",
    "1-2 years",
    "3-5 years",
    "5-10 years",
    "More than 10 years"
  ];

  // Qualification levels
  const qualifications = [
    "High School",
    "Diploma",
    "Bachelor's Degree",
    "Master's Degree",
    "PhD",
    "Other"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
    
    // Clear API error when email is changed
    if (name === "email" && apiErrorMessage) {
      setApiErrorMessage("");
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (!formData.qualification) newErrors.qualification = "Qualification is required";
    if (!formData.tutor_subject) newErrors.tutor_subject = "Subject is required";
    if (!formData.experience) newErrors.experience = "Experience level is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setApiErrorMessage(""); // Clear previous API errors

    try {
      const response = await axios.post(`${API_BASE_URL}/api/tutors`, formData);

      if (response.status === 201) {
        setSubmissionStatus("success");
        // Reset form
        setFormData({
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
          phone: "",
          role: "tutor",
          qualification: "",
          tutor_subject: "",
          experience: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      
      if (error.response) {
        // The server responded with an error status
        const { status, data } = error.response;
        
        if (status === 409 && data.message === "Email already exists") {
          // Handle duplicate email error specifically
          setApiErrorMessage("This email address is already registered. Please use a different email.");
          setErrors({
            ...errors,
            email: "Email already exists"
          });
        } else if (status === 422 && data.errors) {
          // Handle validation errors from Laravel
          setErrors(data.errors);
          setApiErrorMessage("Please fix the validation errors above.");
        } else {
          // Generic error message
          setApiErrorMessage(data.message || "Error registering tutor. Please try again.");
        }
      } else if (error.request) {
        // The request was made but no response was received
        setApiErrorMessage("No response from server. Please check your connection.");
      } else {
        // Something happened in setting up the request
        setApiErrorMessage("An error occurred: " + error.message);
      }
      
      setSubmissionStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4 md:p-6">
      <div className="max-w-3xl mx-auto bg-base-100 rounded-xl shadow-lg overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary to-secondary text-primary-content p-6">
          <h1 className="text-3xl font-bold text-center">Tutor Registration</h1>
          <p className="text-center mt-2 opacity-90">Please fill in all required fields</p>
        </div>

        <div className="p-6 md:p-8">
          {submissionStatus === "success" && (
            <div className="alert alert-success mb-6 shadow-lg">
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-current flex-shrink-0 h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>Tutor registered successfully!</span>
              </div>
            </div>
          )}

          {apiErrorMessage && (
            <div className="alert alert-error mb-6 shadow-lg">
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-current flex-shrink-0 h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{apiErrorMessage}</span>
              
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Full Name <span className="text-error">*</span></span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className={`input input-bordered ${errors.name ? 'input-error' : ''}`}
                />
                {errors.name && <span className="text-error text-sm mt-1">{errors.name}</span>}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Email <span className="text-error">*</span></span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className={`input input-bordered ${errors.email ? 'input-error' : ''}`}
                />
                {errors.email && <span className="text-error text-sm mt-1">{errors.email}</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Password <span className="text-error">*</span></span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  className={`input input-bordered ${errors.password ? 'input-error' : ''}`}
                />
                {errors.password && <span className="text-error text-sm mt-1">{errors.password}</span>}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Confirm Password <span className="text-error">*</span></span>
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className={`input input-bordered ${errors.confirmPassword ? 'input-error' : ''}`}
                />
                {errors.confirmPassword && <span className="text-error text-sm mt-1">{errors.confirmPassword}</span>}
              </div>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Phone Number</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                className="input input-bordered"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Qualification <span className="text-error">*</span></span>
                </label>
                <select
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleChange}
                  className={`select select-bordered ${errors.qualification ? 'select-error' : ''}`}
                >
                  <option value="">Select Qualification</option>
                  {qualifications.map((qualification) => (
                    <option key={qualification} value={qualification}>
                      {qualification}
                    </option>
                  ))}
                </select>
                {errors.qualification && <span className="text-error text-sm mt-1">{errors.qualification}</span>}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Subject <span className="text-error">*</span></span>
                </label>
                <select
                  name="tutor_subject"
                  value={formData.tutor_subject}
                  onChange={handleChange}
                  className={`select select-bordered ${errors.tutor_subject ? 'select-error' : ''}`}
                >
                  <option value="">Select Subject</option>
                  {tutorSubjects.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
                {errors.tutor_subject && <span className="text-error text-sm mt-1">{errors.tutor_subject}</span>}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Experience <span className="text-error">*</span></span>
                </label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className={`select select-bordered ${errors.experience ? 'select-error' : ''}`}
                >
                  <option value="">Select Experience</option>
                  {experienceLevels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
                {errors.experience && <span className="text-error text-sm mt-1">{errors.experience}</span>}
              </div>
            </div>

            <div className="form-control mt-8">
              <button
                type="submit"
                className={`btn btn-primary btn-lg ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? "Registering..." : "Register Tutor"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TutorForm;